import { abweisen, ereignis, type Ereignis, type GespeichertesEreignis } from "../gemeinsam/index.js";
import { E } from "./ereignisse.js";
import { positionPruefen } from "./stand.js";

/**
 * Wertobjekt Revierkarte des Aggregats Revier (Domänenmodell §4: Dateiverweis, Format, optional Georeferenz; ADR-0022).
 * Es gibt genau eine Revierkarte je Revier; eine neue ersetzt die bisherige samt Georeferenz. Das Format wird am
 * Inhalt bestimmt, nie an Dateiname oder angegebenem Inhaltstyp (medien.md §8, revier/08).
 */
export const KARTENFORMATE = ["PDF", "PNG", "JPEG"] as const;
export type Kartenformat = (typeof KARTENFORMATE)[number];

export const ECKEN = ["Nordwest", "Südost"] as const;
export type Ecke = (typeof ECKEN)[number];

export interface Eckkoordinate { Ecke: Ecke; Breite: number; "Länge": number }

export interface RevierkarteZustand {
  /** Dateikennung im Medienspeicher (`reviere/{revierId}/revierkarte/{dateiId}.{endung}`) */
  datei: string | null;
  dateiname: string | null;
  format: Kartenformat | null;
  georeferenz: Eckkoordinate[] | null;
}

export const KEINE_REVIERKARTE: RevierkarteZustand = { datei: null, dateiname: null, format: null, georeferenz: null };

export const DATEIENDUNG: Record<Kartenformat, string> = { PDF: "pdf", PNG: "png", JPEG: "jpg" };
export const INHALTSTYP: Record<Kartenformat, string> = { PDF: "application/pdf", PNG: "image/png", JPEG: "image/jpeg" };

const SIGNATUREN: ReadonlyArray<readonly [Kartenformat, readonly number[]]> = [
  ["PDF", [0x25, 0x50, 0x44, 0x46, 0x2d]],
  ["PNG", [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  ["JPEG", [0xff, 0xd8, 0xff]],
];

/** Bestimmt das Format am Dateianfang (Signatur); `null` für jede andere Datei. */
export function kartenformatAmInhalt(anfang: Uint8Array): Kartenformat | null {
  for (const [format, signatur] of SIGNATUREN) {
    if (anfang.length >= signatur.length && signatur.every((b, i) => anfang[i] === b)) return format;
  }
  return null;
}

export function revierkarteAnwenden(z: RevierkarteZustand, e: GespeichertesEreignis): RevierkarteZustand {
  const d = e.daten;
  switch (e.typ) {
    case E.RevierkarteHochgeladen:
      return { datei: String(d["Revierkarte"]), dateiname: String(d["Dateiname"]), format: d["Format"] as Kartenformat, georeferenz: null };
    case E.RevierkarteGeoreferenziert:
      return z.datei ? { ...z, georeferenz: (d["Ecken"] as Eckkoordinate[]) ?? null } : z;
    default:
      return z;
  }
}

export interface Kartenupload {
  /** Dateikennung, unter der die Karte abgelegt wird */
  datei: string;
  dateiname: unknown;
  /** am Inhalt bestimmt; `null` = keines der bekannten Formate */
  format: Kartenformat | null;
  groesseByte: number;
}

export interface Uploadgrenzen {
  /** Plattformeinstellung „Dateitypen der Revierkarte“ (teststeuerung.md §8) */
  dateitypen: readonly string[];
  /** Plattformeinstellung „Größe der Revierkarte höchstens“ in MB */
  hoechstMb: number;
}

const MB = 1024 * 1024;

function dateinamePruefen(wert: unknown): string {
  const t = typeof wert === "string" ? wert.trim() : "";
  if (!t || t.length > 200 || /[/\\]/.test(t)) abweisen("unzulaessige-angaben");
  return t;
}

/** Revierkarte hochladen (befehle.md §6.2 `RevierkarteHochladen`): erst die Größe, dann das Format am Inhalt. */
export function revierkarteHochladen(u: Kartenupload, grenzen: Uploadgrenzen, handelnd: string): Ereignis[] {
  const dateiname = dateinamePruefen(u.dateiname);
  if (u.groesseByte > grenzen.hoechstMb * MB) abweisen("datei-zu-gross");
  if (!u.format || !grenzen.dateitypen.includes(u.format)) abweisen("dateityp-unzulaessig");
  return [ereignis(E.RevierkarteHochgeladen, { Revierkarte: u.datei, Dateiname: dateiname, Format: u.format, "handelnde Person": handelnd })];
}

/**
 * Revierkarte georeferenzieren (befehle.md §6.2 `RevierkarteGeoreferenzieren`): genau die Ecken Nordwest und Südost,
 * jede im Wertebereich (`position-ungueltig`), Nordwest nördlich und westlich von Südost.
 */
export function revierkarteGeoreferenzieren(z: RevierkarteZustand, eingabe: unknown, handelnd: string): Ereignis[] {
  if (!z.datei) abweisen("revierkarte-fehlt");
  if (!Array.isArray(eingabe) || eingabe.length !== ECKEN.length) abweisen("unzulaessige-angaben");
  const ecken = new Map<Ecke, Eckkoordinate>();
  for (const roh of eingabe as Array<Record<string, unknown>>) {
    const ecke = roh?.["Ecke"];
    if (!(ECKEN as readonly unknown[]).includes(ecke) || ecken.has(ecke as Ecke)) abweisen("unzulaessige-angaben");
    const p = positionPruefen(roh["Breite"], roh["Länge"]);
    ecken.set(ecke as Ecke, { Ecke: ecke as Ecke, Breite: p.breite, "Länge": p.laenge });
  }
  const nw = ecken.get("Nordwest")!;
  const so = ecken.get("Südost")!;
  if (nw.Breite <= so.Breite || nw["Länge"] >= so["Länge"]) abweisen("position-ungueltig");
  const neu = [nw, so];
  const alt = z.georeferenz;
  if (alt && alt.length === 2 && neu.every((e, i) => e.Breite === alt[i]!.Breite && e["Länge"] === alt[i]!["Länge"])) return [];
  return [ereignis(E.RevierkarteGeoreferenziert, { Revierkarte: z.datei, Ecken: neu, "handelnde Person": handelnd })];
}
